import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const generateRateLimitId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const useQueryParamsStore = create(
  persist(
    (set, get) => ({
      token: null,
      issueKey: null,
      // Random id sent to the backend purely for per-technician rate-limiting;
      // unrelated to the real token, generated once per (token, issueKey) pairing.
      rateLimitId: null,

      // Action to set both values. Only generates a new rateLimitId when the
      // pairing actually changes — initialize() re-parses the URL on every tab
      // mount and calls this with the same values each time. Backfills
      // rateLimitId on the unchanged-pairing path too, in case it's still null
      // from a rehydrate of storage persisted before this field existed.
      setParams: (token, issueKey) => set((state) =>
        state.token === token && state.issueKey === issueKey
          ? { token, issueKey, rateLimitId: state.rateLimitId ?? generateRateLimitId() }
          : { token, issueKey, rateLimitId: generateRateLimitId() }
      ),

      // Action to clear both values
      clearParams: () => set({ token: null, issueKey: null, rateLimitId: null }),

      // Check if both params are set
      hasParams: () => !!get().token && !!get().issueKey,
    }),
    {
      name: 'query-params-storage',
      // sessionStorage is scoped to this single tab/window — unlike
      // IndexedDB/localStorage it is never shared with other tabs on the same
      // origin, so one ticket's token/issueKey can't leak into another
      // ticket's tab on refresh. It still survives a reload of this same tab.
      storage: createJSONStorage(() => sessionStorage)
    }
  )
);